import React, {ReactNode, forwardRef, useImperativeHandle, useRef} from 'react';
import TeamEmptyState from './team-empty-state';
import ErrorPages from '@/components/basic/error-pages';
import {ProgressiveCircularLoading} from '@fe-progressive/package-progressive/progressive-loading';

export interface TeamBodyTitle {
  text: string | ReactNode;
  className?: string;
}

export interface TeamTableBodyProps<T> {
  loading?: boolean;
  data: T[];
  rowKey?: (item: T) => string;
  bodyCell: ((item: T) => TeamBodyTitle)[];
  onScroll?: (scrollLeft: number) => void;
  showSearchEmpty?: boolean;
}

export interface TeamTableScrollRef {
  scrollTo: (left: number) => void;
}

function TeamTableBody<T = any>(
  props: TeamTableBodyProps<T>,
  ref: React.ForwardedRef<TeamTableScrollRef>,
) {
  const {data, rowKey, bodyCell, onScroll, loading, showSearchEmpty} = props;
  const scrollRef = useRef<HTMLDivElement>(null);

  const handleTableBodyScroll = () => {
    const scrollLeft = scrollRef.current?.scrollLeft;
    onScroll?.(scrollLeft || 0);
  };

  useImperativeHandle(
    ref,
    () => {
      return {
        scrollTo(left: number) {
          scrollRef.current?.scrollTo({left});
        },
      };
    },
    [],
  );

  return (
    <div
      className="flex flex-col items-start flex-none overflow-x-auto"
      ref={scrollRef}
      onScroll={handleTableBodyScroll}>
      {data.length === 0 ? (
        <div className="w-full flex justify-center items-center bg-white min-h-[18rem]">
          {showSearchEmpty ? <ErrorPages.NoResult /> : <TeamEmptyState />}
        </div>
      ) : (
        data.map((row, rowIndex) => (
          <div
            key={rowKey ? rowKey(row) : rowIndex}
            className="flex flex-row h-10 gap-x-2 even:bg-white odd:bg-gray-50 text-sm text-gray-600">
            {bodyCell.map((cellFn, cellIndex) => {
              const cellItem = cellFn(row);
              return (
                <div
                  key={cellIndex}
                  className={`${
                    cellIndex === 0
                      ? `border-r border-white sticky left-0 ${
                          rowIndex % 2 === 1 ? 'bg-white' : 'bg-gray-50'
                        }`
                      : ''
                  } flex-none flex items-center ${cellItem.className}`}>
                  {cellItem.text}
                </div>
              );
            })}
          </div>
        ))
      )}
      {loading ? (
        <div className="h-[6.25rem] w-full flex justify-center items-center">
          <ProgressiveCircularLoading size={24} />
        </div>
      ) : null}
    </div>
  );
}

export default forwardRef(TeamTableBody);
